import React, { useState, useEffect, useContext, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import "./Chatbot.scss";
import ChatSidebar from "./ChatSidebar";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import ReactMarkdown from "react-markdown";
import axios from "axios";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import Navbar from "../navbar/Navbar";
import { AuthContext } from "../../Context/AuthContext";
import OnboardingTour from "./OnboardingTour";
import { buildTimelineCalendar, getFixedTimelineItems } from "./timelineCalendar";
import {
  getCurrentUser,
  getOAuthCallbackInfo,
  resolveOAuthErrorRedirect,
} from "../../Supabase/Config";

const TRANSLATIONS = {
  en: {
    welcome: "Welcome to RegalBot",
    welcomeSubtitle: "I'm your AI legal assistant. Select your role above for tailored responses.",
    placeholder: "Write your message...",
    suggestedQuestions: "Suggested questions:",
    verifiedSourcesTitle: "Verified Sources",
    verifiedBadge: "Verified",
    documentRefsTitle: "From your document",
    askAboutDoc: "Ask about this document",
    activeDocLabel: "Chatting with",
    clearDoc: "Clear document",
    docPlaceholder: "Ask about your document...",
    tour: {
      launch: "Take the tour",
      next: "Next",
      back: "Back",
      skip: "Skip",
      done: "Got it",
      welcomeTitle: "Welcome to RegalBot 👋",
      welcomeBody: "Let's take a quick 60-second tour to show you how to get the most out of your AI legal assistant.",
      langTitle: "Choose your language",
      langBody: "Switch the entire experience between English, French, and Chinese anytime.",
      roleTitle: "Pick your role",
      roleBody: "Tell us if you're a General user, Paralegal, or Lawyer — answers adapt to your level.",
      askTitle: "Ask a legal question",
      askBody: "Type any Canadian legal question here and press send to get a tailored answer.",
      sourcesTitle: "Verified Sources",
      sourcesBody: "Every answer cites real, clickable Canadian legal sources — no invented citations.",
      followTitle: "Suggested follow-ups",
      followBody: "After each answer we suggest smart follow-up questions to guide your research.",
      docTitle: "Analyze a document",
      docBody: "Upload a PDF or TXT contract for an instant risk analysis (20 tokens).",
      templatesTitle: "Legal templates",
      templatesBody: "Generate ready-to-use documents like NDAs and demand letters (15 tokens).",
      pdfTitle: "Export to PDF",
      pdfBody: "Download your whole conversation as a polished PDF whenever you like.",
      tokensTitle: "Your tokens",
      tokensBody: "Tokens power each action. Keep an eye on your balance here.",
    },
    disclaimer: "RegalBOT provides legal information, not legal advice. Always consult a licensed lawyer for professional legal guidance.",
    exportPdf: "Export Chat as PDF",
    templates: "Legal Templates",
    analyzeDoc: "Analyze Document (20 tokens)",
    chooseTemplate: "Choose a Legal Template",
    chooseTemplateSubtitle: "Select a document type to generate a customized template",
    fillDetails: "Fill in the details",
    generate: "Generate Document (15 tokens)",
    generating: "Generating...",
    back: "Back",
    download: "Download as PDF",
    close: "Close",
    userTypes: {
      general: 'General User',
      paralegal: 'Paralegal',
      lawyer: 'Lawyer'
    },
    userTypeDescriptions: {
      general: 'Plain language explanations',
      paralegal: 'Procedural focus',
      lawyer: 'Technical legal analysis'
    },
    categories: {
      contract: 'Contract Law', immigration: 'Immigration', business: 'Business Law',
      family: 'Family Law', employment: 'Employment Law', real_estate: 'Real Estate',
      criminal: 'Criminal Law', intellectual_property: 'IP Law', personal_injury: 'Personal Injury',
      estate_planning: 'Estate Planning', general: 'General Legal'
    },
    pdfTitle: 'RegalBot Legal Consultation',
    pdfDate: 'Date',
    pdfUser: 'User',
    pdfBot: 'RegalBot',
    pdfFooter: 'Legal information only - not legal advice. Consult a licensed lawyer.',
    noMessages: 'No conversation to export',
    generated: 'Generated',
    insufficientTokens: 'Template generation requires 15 tokens.',
    failedGenerate: 'Failed to generate template',
    urgent: '⚠️ Urgent',
    timeSensitive: '⏰ Time-sensitive',
    docModal: {
      title: 'Document Analysis',
      summary: 'Summary',
      keyParties: 'Key Parties',
      riskAssessment: 'Risk Assessment',
      recommendation: 'Recommendation',
      keyClauses: 'Key Clauses',
      missingClauses: 'Missing Clauses',
      recommendations: 'Recommendations',
      documentDefault: 'Document'
    },
    compare: 'Compare Documents',
    compareModal: {
      title: 'Compare Two Documents',
      subtitle: 'Upload two versions (PDF/TXT) to see what changed, in plain language.',
      docA: 'Original document',
      docB: 'Revised document',
      chooseFile: 'Choose file',
      runCompare: 'Compare (25 tokens)',
      comparing: 'Comparing...',
      summary: 'Summary of Changes',
      differences: 'Key Differences',
      added: 'Added',
      removed: 'Removed',
      changed: 'Changed',
      risk: 'Risk',
      noDifferences: 'No substantive differences were found between the two documents.',
      missingInA: 'Missing from the original',
      missingInB: 'Missing from the revised',
      exportPdf: 'Download Comparison as PDF',
      newCompare: 'Compare other documents',
      insufficientTokens: 'Document comparison requires 25 tokens.',
      failed: 'Failed to compare documents. Please try again.',
      needTwo: 'Please choose two documents to compare.',
      pdfTitle: 'RegalBot Document Comparison'
    },
    heatmap: {
      tabAnalysis: 'Analysis',
      tabHeatmap: 'Risk Heatmap',
      overallRisk: 'Overall risk',
      score: 'Risk score',
      high: 'High risk',
      medium: 'Medium risk',
      low: 'Low risk',
      clickHint: 'Click any clause to see what it means and how to make it safer.',
      meaning: 'What this clause means',
      whyRisky: 'Why it matters',
      suggestion: 'Suggested safer wording',
      copy: 'Copy suggestion',
      copied: 'Copied!',
      clause: 'Clause',
      demoButton: 'Try demo contract',
      demoFailed: 'Failed to load the demo contract. Please try again.',
      demoButtonTitle: 'Analyze a built-in sample contract to see the Risk Heatmap in action — free',
      unassessed: 'Not assessed',
      partialNotice: 'Some clauses could not be assessed and are marked accordingly.',
      unavailable: 'The risk heatmap could not be generated for this document.'
    },
    timeline: {
      tab: 'Key Dates & Actions',
      title: 'Contract commitments',
      subtitle: 'Dates, notice windows, payments, renewals, and actions found in this document.',
      all: 'All commitments',
      upcoming: 'Upcoming',
      payments: 'Payments',
      renewals: 'Renewals',
      fixed: 'Fixed dates',
      relative: 'Relative deadlines',
      recurring: 'Recurring',
      noDate: 'No fixed date',
      uncertain: 'Needs review',
      uncertainNotice: 'Some possible commitments could not be fully verified. Review the source contract for anything missing or marked “Needs review.”',
      unavailable: 'A commitments timeline could not be generated for this document.',
      empty: 'No commitments match this filter.',
      exportCalendar: 'Add dates to calendar',
      exportHint: 'Only commitments with a confirmed calendar date are included.',
      sourceClause: 'Source clause',
      responsible: 'Responsible',
      urgency: 'Urgency',
      confidence: 'Confidence',
      high: 'High',
      medium: 'Medium',
      low: 'Low',
      past: 'Past',
      today: 'Today',
      categories: {
        payment: 'Payment',
        renewal: 'Renewal',
        termination: 'Termination',
        notice: 'Notice',
        deadline: 'Deadline',
        obligation: 'Obligation'
      }
    },
    voice: {
      startListening: 'Speak your question',
      listening: 'Listening… tap to stop',
      readAloud: 'Read aloud',
      stop: 'Stop',
      autoRead: 'Auto-read',
      autoReadOn: 'Auto-read on',
      autoReadOff: 'Auto-read off',
      unsupported: 'Voice input isn’t supported in this browser.'
    }
  },
  fr: {
    welcome: "Bienvenue à RegalBot",
    welcomeSubtitle: "Je suis votre assistant juridique IA. Sélectionnez votre rôle ci-dessus pour des réponses adaptées.",
    placeholder: "Écrivez votre message...",
    suggestedQuestions: "Questions suggérées :",
    verifiedSourcesTitle: "Sources vérifiées",
    verifiedBadge: "Vérifié",
    documentRefsTitle: "Extrait de votre document",
    askAboutDoc: "Poser une question sur ce document",
    activeDocLabel: "Conversation avec",
    clearDoc: "Effacer le document",
    docPlaceholder: "Posez une question sur votre document...",
    tour: {
      launch: "Visite guidée",
      next: "Suivant",
      back: "Retour",
      skip: "Passer",
      done: "Compris",
      welcomeTitle: "Bienvenue à RegalBot 👋",
      welcomeBody: "Faisons une visite rapide de 60 secondes pour vous montrer comment tirer le meilleur parti de votre assistant juridique IA.",
      langTitle: "Choisissez votre langue",
      langBody: "Basculez toute l'expérience entre l'anglais, le français et le chinois à tout moment.",
      roleTitle: "Choisissez votre rôle",
      roleBody: "Indiquez si vous êtes un utilisateur général, un parajuriste ou un avocat — les réponses s'adaptent à votre niveau.",
      askTitle: "Posez une question juridique",
      askBody: "Tapez n'importe quelle question juridique canadienne ici et envoyez pour obtenir une réponse adaptée.",
      sourcesTitle: "Sources vérifiées",
      sourcesBody: "Chaque réponse cite de vraies sources juridiques canadiennes cliquables — aucune citation inventée.",
      followTitle: "Questions suggérées",
      followBody: "Après chaque réponse, nous suggérons des questions de suivi pertinentes pour guider vos recherches.",
      docTitle: "Analyser un document",
      docBody: "Téléversez un contrat PDF ou TXT pour une analyse de risque instantanée (20 jetons).",
      templatesTitle: "Modèles juridiques",
      templatesBody: "Générez des documents prêts à l'emploi comme des NDA et des mises en demeure (15 jetons).",
      pdfTitle: "Exporter en PDF",
      pdfBody: "Téléchargez toute votre conversation sous forme de PDF soigné quand vous le souhaitez.",
      tokensTitle: "Vos jetons",
      tokensBody: "Les jetons alimentent chaque action. Surveillez votre solde ici.",
    },
    disclaimer: "RegalBOT fournit de l'information juridique, et non des conseils juridiques. Consultez toujours un avocat qualifié.",
    exportPdf: "Exporter la conversation en PDF",
    templates: "Modèles juridiques",
    analyzeDoc: "Analyser un document (20 jetons)",
    chooseTemplate: "Choisir un modèle juridique",
    chooseTemplateSubtitle: "Sélectionnez un type de document pour générer un modèle personnalisé",
    fillDetails: "Remplissez les détails",
    generate: "Générer le document (15 jetons)",
    generating: "Génération...",
    back: "Retour",
    download: "Télécharger en PDF",
    close: "Fermer",
    userTypes: {
      general: 'Utilisateur général',
      paralegal: 'Parajuriste',
      lawyer: 'Avocat'
    },
    userTypeDescriptions: {
      general: 'Explications en langage simple',
      paralegal: 'Concentration procédurale',
      lawyer: 'Analyse juridique technique'
    },
    categories: {
      contract: 'Droit des contrats', immigration: 'Immigration', business: 'Droit des affaires',
      family: 'Droit de la famille', employment: 'Droit du travail', real_estate: 'Immobilier',
      criminal: 'Droit pénal', intellectual_property: 'Propriété intellectuelle', personal_injury: 'Préjudice corporel',
      estate_planning: 'Planification successorale', general: 'Juridique général'
    },
    pdfTitle: 'Consultation juridique RegalBot',
    pdfDate: 'Date',
    pdfUser: 'Utilisateur',
    pdfBot: 'RegalBot',
    pdfFooter: 'Information juridique seulement - pas un conseil juridique. Consultez un avocat qualifié.',
    noMessages: 'Aucune conversation à exporter',
    generated: 'Généré',
    insufficientTokens: 'La génération du modèle nécessite 15 jetons.',
    failedGenerate: 'Échec de la génération du modèle',
    urgent: '⚠️ Urgent',
    timeSensitive: '⏰ Sensible au temps',
    docModal: {
      title: 'Analyse du document',
      summary: 'Résumé',
      keyParties: 'Parties clés',
      riskAssessment: 'Évaluation des risques',
      recommendation: 'Recommandation',
      keyClauses: 'Clauses clés',
      missingClauses: 'Clauses manquantes',
      recommendations: 'Recommandations',
      documentDefault: 'Document'
    },
    compare: 'Comparer des documents',
    compareModal: {
      title: 'Comparer deux documents',
      subtitle: 'Téléversez deux versions (PDF/TXT) pour voir ce qui a changé, en langage simple.',
      docA: 'Document original',
      docB: 'Document révisé',
      chooseFile: 'Choisir un fichier',
      runCompare: 'Comparer (25 jetons)',
      comparing: 'Comparaison...',
      summary: 'Résumé des modifications',
      differences: 'Principales différences',
      added: 'Ajouté',
      removed: 'Supprimé',
      changed: 'Modifié',
      risk: 'Risque',
      noDifferences: 'Aucune différence importante n\'a été trouvée entre les deux documents.',
      missingInA: 'Absent du document original',
      missingInB: 'Absent du document révisé',
      exportPdf: 'Télécharger la comparaison en PDF',
      newCompare: 'Comparer d\'autres documents',
      insufficientTokens: 'La comparaison de documents nécessite 25 jetons.',
      failed: 'Échec de la comparaison des documents. Veuillez réessayer.',
      needTwo: 'Veuillez choisir deux documents à comparer.',
      pdfTitle: 'Comparaison de documents RegalBot'
    },
    heatmap: {
      tabAnalysis: 'Analyse',
      tabHeatmap: 'Carte des risques',
      overallRisk: 'Risque global',
      score: 'Score de risque',
      high: 'Risque élevé',
      medium: 'Risque moyen',
      low: 'Risque faible',
      clickHint: 'Cliquez sur une clause pour voir ce qu\'elle signifie et comment la sécuriser.',
      meaning: 'Ce que signifie cette clause',
      whyRisky: 'Pourquoi c\'est important',
      suggestion: 'Formulation plus sûre suggérée',
      copy: 'Copier la suggestion',
      copied: 'Copié !',
      clause: 'Clause',
      demoButton: 'Essayer le contrat de démonstration',
      demoFailed: 'Échec du chargement du contrat de démonstration. Veuillez réessayer.',
      demoButtonTitle: 'Analysez un contrat d\'exemple intégré pour voir la carte des risques en action — gratuit',
      unassessed: 'Non évalué',
      partialNotice: 'Certaines clauses n\'ont pas pu être évaluées et sont signalées comme telles.',
      unavailable: 'La carte des risques n\'a pas pu être générée pour ce document.'
    },
    timeline: {
      tab: 'Dates et actions clés',
      title: 'Engagements contractuels',
      subtitle: 'Dates, délais de préavis, paiements, renouvellements et actions repérés dans ce document.',
      all: 'Tous les engagements',
      upcoming: 'À venir',
      payments: 'Paiements',
      renewals: 'Renouvellements',
      fixed: 'Dates fixes',
      relative: 'Délais relatifs',
      recurring: 'Récurrents',
      noDate: 'Aucune date fixe',
      uncertain: 'À vérifier',
      uncertainNotice: 'Certains engagements possibles n’ont pas pu être entièrement vérifiés. Consultez le contrat source pour tout élément manquant ou marqué « À vérifier ».',
      unavailable: 'Impossible de générer un échéancier des engagements pour ce document.',
      empty: 'Aucun engagement ne correspond à ce filtre.',
      exportCalendar: 'Ajouter les dates au calendrier',
      exportHint: 'Seuls les engagements ayant une date civile confirmée sont inclus.',
      sourceClause: 'Clause source',
      responsible: 'Responsable',
      urgency: 'Priorité',
      confidence: 'Confiance',
      high: 'Élevée',
      medium: 'Moyenne',
      low: 'Faible',
      past: 'Passé',
      today: 'Aujourd’hui',
      categories: {
        payment: 'Paiement',
        renewal: 'Renouvellement',
        termination: 'Résiliation',
        notice: 'Préavis',
        deadline: 'Échéance',
        obligation: 'Obligation'
      }
    },
    voice: {
      startListening: 'Posez votre question à voix haute',
      listening: 'Écoute… touchez pour arrêter',
      readAloud: 'Lire à voix haute',
      stop: 'Arrêter',
      autoRead: 'Lecture auto',
      autoReadOn: 'Lecture auto activée',
      autoReadOff: 'Lecture auto désactivée',
      unsupported: 'La saisie vocale n’est pas prise en charge dans ce navigateur.'
    }
  },
  zh: {
    welcome: "欢迎使用 RegalBot",
    welcomeSubtitle: "我是您的人工智能法律助手。请在上方选择您的身份以获取定制化回复。",
    placeholder: "请输入您的问题...",
    suggestedQuestions: "推荐问题：",
    verifiedSourcesTitle: "已核实来源",
    verifiedBadge: "已核实",
    documentRefsTitle: "来自您的文档",
    askAboutDoc: "就此文档提问",
    activeDocLabel: "正在咨询",
    clearDoc: "清除文档",
    docPlaceholder: "就您的文档提问……",
    tour: {
      launch: "新手引导",
      next: "下一步",
      back: "上一步",
      skip: "跳过",
      done: "我知道了",
      welcomeTitle: "欢迎使用 RegalBot 👋",
      welcomeBody: "让我们用 60 秒快速浏览一下，了解如何充分利用您的人工智能法律助手。",
      langTitle: "选择您的语言",
      langBody: "随时在英语、法语和中文之间切换整个界面。",
      roleTitle: "选择您的身份",
      roleBody: "告诉我们您是普通用户、律师助理还是律师——回复会根据您的水平进行调整。",
      askTitle: "提出法律问题",
      askBody: "在此输入任何加拿大法律问题并发送，即可获得量身定制的回答。",
      sourcesTitle: "已核实来源",
      sourcesBody: "每个回答都会引用真实、可点击的加拿大法律来源——绝无虚构引用。",
      followTitle: "推荐的后续问题",
      followBody: "每次回答后，我们都会推荐相关的后续问题来引导您的研究。",
      docTitle: "分析文档",
      docBody: "上传 PDF 或 TXT 合同，即可即时进行风险分析（20 令牌）。",
      templatesTitle: "法律模板",
      templatesBody: "生成可直接使用的文档，如保密协议和催款函（15 令牌）。",
      pdfTitle: "导出为 PDF",
      pdfBody: "随时将整个对话下载为精美的 PDF。",
      tokensTitle: "您的令牌",
      tokensBody: "令牌为每个操作提供支持。请在此处留意您的余额。",
    },
    disclaimer: "RegalBOT 仅提供法律信息，不构成法律意见。请务必咨询持牌律师以获得专业法律指导。",
    exportPdf: "导出对话为 PDF",
    templates: "法律模板",
    analyzeDoc: "分析文档（20 令牌）",
    chooseTemplate: "选择一个法律模板",
    chooseTemplateSubtitle: "选择文档类型以生成定制化模板",
    fillDetails: "请填写详细信息",
    generate: "生成文档（15 令牌）",
    generating: "生成中...",
    back: "返回",
    download: "下载为 PDF",
    close: "关闭",
    userTypes: {
      general: '普通用户',
      paralegal: '律师助理',
      lawyer: '律师'
    },
    userTypeDescriptions: {
      general: '通俗易懂的解释',
      paralegal: '侧重程序事项',
      lawyer: '专业法律分析'
    },
    categories: {
      contract: '合同法', immigration: '移民法', business: '商业法',
      family: '家庭法', employment: '劳动法', real_estate: '房地产',
      criminal: '刑法', intellectual_property: '知识产权', personal_injury: '人身伤害',
      estate_planning: '遗产规划', general: '一般法律'
    },
    pdfTitle: 'RegalBot 法律咨询',
    pdfDate: '日期',
    pdfUser: '用户',
    pdfBot: 'RegalBot',
    pdfFooter: '仅供参考的法律信息 - 不构成法律意见。请咨询持牌律师。',
    noMessages: '没有可导出的对话',
    generated: '已生成',
    insufficientTokens: '生成模板需要 15 个令牌。',
    failedGenerate: '生成模板失败',
    urgent: '⚠️ 紧急',
    timeSensitive: '⏰ 时间敏感',
    docModal: {
      title: '文档分析',
      summary: '摘要',
      keyParties: '关键当事方',
      riskAssessment: '风险评估',
      recommendation: '建议',
      keyClauses: '关键条款',
      missingClauses: '缺失条款',
      recommendations: '建议事项',
      documentDefault: '文档'
    },
    compare: '比较文档',
    compareModal: {
      title: '比较两份文档',
      subtitle: '上传两个版本（PDF/TXT），以通俗易懂的方式查看变更内容。',
      docA: '原始文档',
      docB: '修订文档',
      chooseFile: '选择文件',
      runCompare: '比较（25 令牌）',
      comparing: '比较中...',
      summary: '变更摘要',
      differences: '主要差异',
      added: '新增',
      removed: '删除',
      changed: '修改',
      risk: '风险',
      noDifferences: '两份文档之间未发现实质性差异。',
      missingInA: '原始文档中缺失',
      missingInB: '修订文档中缺失',
      exportPdf: '下载比较结果为 PDF',
      newCompare: '比较其他文档',
      insufficientTokens: '文档比较需要 25 个令牌。',
      failed: '比较文档失败。请重试。',
      needTwo: '请选择两份要比较的文档。',
      pdfTitle: 'RegalBot 文档比较'
    },
    heatmap: {
      tabAnalysis: '分析',
      tabHeatmap: '风险热图',
      overallRisk: '整体风险',
      score: '风险评分',
      high: '高风险',
      medium: '中等风险',
      low: '低风险',
      clickHint: '点击任意条款，查看其含义以及如何使其更安全。',
      meaning: '此条款的含义',
      whyRisky: '为什么重要',
      suggestion: '建议的更安全措辞',
      copy: '复制建议',
      copied: '已复制！',
      clause: '条款',
      demoButton: '试用演示合同',
      demoFailed: '演示合同加载失败，请重试。',
      demoButtonTitle: '分析内置示例合同，免费体验风险热图',
      unassessed: '未评估',
      partialNotice: '部分条款无法评估，已相应标注。',
      unavailable: '无法为此文档生成风险热图。'
    },
    timeline: {
      tab: '关键日期与行动',
      title: '合同承诺',
      subtitle: '从文档中识别出的日期、通知期限、付款、续约和行动事项。',
      all: '全部承诺',
      upcoming: '即将到期',
      payments: '付款',
      renewals: '续约',
      fixed: '固定日期',
      relative: '相对期限',
      recurring: '定期事项',
      noDate: '无固定日期',
      uncertain: '需要核实',
      uncertainNotice: '部分潜在承诺无法得到充分核实。请查看源合同中可能遗漏或标记为“需要核实”的内容。',
      unavailable: '无法为此文档生成合同承诺时间线。',
      empty: '没有符合此筛选条件的承诺事项。',
      exportCalendar: '将日期添加到日历',
      exportHint: '仅包含已确认具体日历日期的承诺事项。',
      sourceClause: '来源条款',
      responsible: '责任方',
      urgency: '优先级',
      confidence: '置信度',
      high: '高',
      medium: '中',
      low: '低',
      past: '已过期',
      today: '今天',
      categories: {
        payment: '付款',
        renewal: '续约',
        termination: '终止',
        notice: '通知',
        deadline: '截止日期',
        obligation: '义务'
      }
    },
    voice: {
      startListening: '说出您的问题',
      listening: '正在聆听……点击停止',
      readAloud: '朗读',
      stop: '停止',
      autoRead: '自动朗读',
      autoReadOn: '自动朗读已开启',
      autoReadOff: '自动朗读已关闭',
      unsupported: '此浏览器不支持语音输入。'
    }
  }
};

const USER_TYPES = [
  { id: 'general', icon: '👤' },
  { id: 'paralegal', icon: '📋' },
  { id: 'lawyer', icon: '⚖️' }
];

const Chatbot = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [savedChats, setSavedChats] = useState([]);
  const [tokens, setTokens] = useState();
  const [chatUuid, setChatUuid] = useState();
  const [loading, setLoading] = useState(false); 
  const [packageTitle, setPackageTitle] = useState(null);
  const [userType, setUserType] = useState('general');
  const [followUpQuestions, setFollowUpQuestions] = useState([]);
  const [lastClassification, setLastClassification] = useState(null);
  const [showUserTypeSelector, setShowUserTypeSelector] = useState(false);
  const [documentAnalysis, setDocumentAnalysis] = useState(null);
  const [analyzingDocument, setAnalyzingDocument] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [docModalTab, setDocModalTab] = useState('analysis');
  const [selectedClauseIndex, setSelectedClauseIndex] = useState(null);
  const [copiedClauseIndex, setCopiedClauseIndex] = useState(null);
  const [timelineFilter, setTimelineFilter] = useState('all');
  const [activeDocument, setActiveDocument] = useState(null);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [compareFileA, setCompareFileA] = useState(null);
  const [compareFileB, setCompareFileB] = useState(null);
  const [comparisonResult, setComparisonResult] = useState(null);
  const [comparingDocuments, setComparingDocuments] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [speakingIndex, setSpeakingIndex] = useState(null);
  const [autoRead, setAutoRead] = useState(() => {
    try { return localStorage.getItem('regalbot_autoread') === '1'; } catch (e) { return false; }
  });
  const recognitionRef = useRef(null);
  const listeningBaseRef = useRef("");
  const lastSpokenRef = useRef(-1);
  const [language, setLanguage] = useState(() => {
    try {
      const urlLang = new URLSearchParams(window.location.search).get('lang');
      if (urlLang && ['en', 'fr', 'zh'].includes(urlLang)) return urlLang;
    } catch (e) {}
    return localStorage.getItem('regalbot_lang') || 'en';
  });
  const [showTemplatesModal, setShowTemplatesModal] = useState(false);
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateFieldValues, setTemplateFieldValues] = useState({});
  const [generatedDocument, setGeneratedDocument] = useState(null);
  const [generatingTemplate, setGeneratingTemplate] = useState(false);
  const [runTour, setRunTour] = useState(false);
  const [tourUserId, setTourUserId] = useState(null);

  const t = TRANSLATIONS[language] || TRANSLATIONS.en;

  const { _auth, loading: authLoading } = useContext(AuthContext);
  const navigate = useNavigate();

  // Track whether this page load is an OAuth provider redirect, so we can show
  // a loading state while Supabase resolves the session instead of flashing the
  // unauthenticated UI.
  const [oauthProcessing, setOauthProcessing] = useState(
    () => getOAuthCallbackInfo().hasAuth
  );

  // Handle the OAuth callback that lands on /chatbot after the provider redirect.
  // On a provider error, forward the message to the screen the user started from
  // (Login or Sign Up) so they see why social login failed instead of being
  // stranded on the chatbot page.
  useEffect(() => {
    const redirectTo = resolveOAuthErrorRedirect();
    if (redirectTo) {
      navigate(redirectTo, { replace: true });
    }
  }, [navigate]);

  // Once Supabase finishes resolving the redirected session, drop the loader.
  useEffect(() => {
    if (oauthProcessing && !authLoading) {
      setOauthProcessing(false);
    }
  }, [oauthProcessing, authLoading]);

  const tourStorageKey = (id) => `regalbot_tour_done_${id || 'anon'}`;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let id = 'anon';
      try {
        const user = await getCurrentUser();
        if (user && user.id) id = user.id;
      } catch (e) {}
      if (cancelled) return;
      setTourUserId(id);
      try {
        // Skip the auto-launched tour when arriving via a demo deep-link so
        // the demo content isn't hidden behind the tour overlay.
        const isDemoLink = new URLSearchParams(window.location.search).has('demo');
        const done = localStorage.getItem(tourStorageKey(id));
        if (!done && !isDemoLink) setRunTour(true);
      } catch (e) {}
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const finishTour = () => {
    setRunTour(false);
    try {
      localStorage.setItem(tourStorageKey(tourUserId), '1');
    } catch (e) {}
  };

  const startTour = () => setRunTour(true);

  const tourSteps = [
    { title: t.tour.welcomeTitle, body: t.tour.welcomeBody },
    { selector: '[data-tour="navbar-language"]', fallbackSelector: '[data-tour="language-toggle"]', title: t.tour.langTitle, body: t.tour.langBody },
    { selector: '[data-tour="user-type"]', title: t.tour.roleTitle, body: t.tour.roleBody },
    { selector: '[data-tour="message-input"]', title: t.tour.askTitle, body: t.tour.askBody },
    { selector: '[data-tour="verified-sources"]', fallbackSelector: '[data-tour="conversation"]', title: t.tour.sourcesTitle, body: t.tour.sourcesBody },
    { selector: '[data-tour="follow-ups"]', fallbackSelector: '[data-tour="conversation"]', title: t.tour.followTitle, body: t.tour.followBody },
    { selector: '[data-tour="document-upload"]', title: t.tour.docTitle, body: t.tour.docBody },
    { selector: '[data-tour="templates"]', title: t.tour.templatesTitle, body: t.tour.templatesBody },
    { selector: '[data-tour="export-pdf"]', title: t.tour.pdfTitle, body: t.tour.pdfBody },
    { selector: '[data-tour="tokens"]', title: t.tour.tokensTitle, body: t.tour.tokensBody },
  ];

  useEffect(() => {
    axios.post(
      `${process.env.REACT_APP_API_URL}/api/set_language`,
      { language },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("jwt")}`,
        },
      }
    ).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  // Keep chat + tour language in sync when it's changed elsewhere (e.g. Navbar
  // toggle), which dispatches a 'regalbot-language-change' event / updates
  // localStorage. Without this, tour copy wouldn't update live across the app.
  useEffect(() => {
    const onLangChange = (e) => {
      const lang = e.detail && e.detail.language;
      if (lang && ['en', 'fr', 'zh'].includes(lang)) {
        setLanguage((prev) => (prev !== lang ? lang : prev));
      }
    };
    const onStorage = (e) => {
      if (e.key === 'regalbot_lang' && e.newValue && ['en', 'fr', 'zh'].includes(e.newValue)) {
        setLanguage((prev) => (prev !== e.newValue ? e.newValue : prev));
      }
    };
    window.addEventListener('regalbot-language-change', onLangChange);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('regalbot-language-change', onLangChange);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const initialChats = {
    "Today History": [],
    "Recent Chats": [],
  };

  const [chats, setChats] = useState(initialChats);

  const sliderSettings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    adaptiveHeight: true,
    autoplay: true,
    autoplaySpeed: 3000,
  };

  const handleUserTypeChange = async (type) => {
    setUserType(type);
    setShowUserTypeSelector(false);
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/set_user_type`,
        { type },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("jwt")}`,
          },
        }
      );
    } catch (error) {
      console.error("Error setting user type:", error);
    }
  };

  const handleFollowUpClick = (question) => {
    setInput(question);
  };

  const handleLanguageChange = async (lang) => {
    setLanguage(lang);
    localStorage.setItem('regalbot_lang', lang);
    window.dispatchEvent(new CustomEvent('regalbot-language-change', { detail: { language: lang } }));
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/set_language`,
        { language: lang },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("jwt")}`,
          },
        }
      );
    } catch (error) {
      console.error("Error setting language:", error);
    }
  };

  const renderChatPDFFromHTML = async (msgs, tr, dateString) => {
    const container = document.createElement('div');
    container.style.position = 'fixed';
    container.style.left = '-10000px';
    container.style.top = '0';
    container.style.width = '780px';
    container.style.padding = '0';
    container.style.background = '#ffffff';
    container.style.fontFamily = "'PingFang SC','Microsoft YaHei','Hiragino Sans GB','Source Han Sans SC','Noto Sans CJK SC','WenQuanYi Micro Hei',Arial,sans-serif";
    container.style.color = '#222';
    container.style.fontSize = '14px';
    container.style.lineHeight = '1.55';

    const header = document.createElement('div');
    header.style.background = '#4a6fdc';
    header.style.color = '#ffffff';
    header.style.padding = '18px 24px';
    header.style.fontSize = '20px';
    header.style.fontWeight = 'bold';
    header.textContent = tr.pdfTitle;
    container.appendChild(header);

    const dateEl = document.createElement('div');
    dateEl.style.padding = '12px 24px 6px';
    dateEl.style.color = '#555';
    dateEl.style.fontSize = '12px';
    dateEl.style.borderBottom = '1px solid #ddd';
    dateEl.textContent = `${tr.pdfDate}: ${dateString}`;
    container.appendChild(dateEl);

    const body = document.createElement('div');
    body.style.padding = '16px 24px';
    msgs.forEach((msg) => {
      const isUser = msg.sender === 'user';
      const wrap = document.createElement('div');
      wrap.style.marginBottom = '14px';

      const lbl = document.createElement('div');
      lbl.style.fontWeight = 'bold';
      lbl.style.fontSize = '13px';
      lbl.style.color = isUser ? '#1e64c8' : '#4a6fdc';
      lbl.style.marginBottom = '4px';
      lbl.textContent = isUser ? tr.pdfUser : tr.pdfBot;
      wrap.appendChild(lbl);

      const txt = document.createElement('div');
      txt.style.color = '#222';
      txt.style.whiteSpace = 'pre-wrap';
      txt.style.wordBreak = 'break-word';
      txt.textContent = (msg.text || '').replace(/\*\*/g, '').replace(/\*/g, '').replace(/---/g, '');
      wrap.appendChild(txt);
      body.appendChild(wrap);
    });
    container.appendChild(body);

    const footer = document.createElement('div');
    footer.style.padding = '10px 24px 18px';
    footer.style.borderTop = '1px solid #ddd';
    footer.style.color = '#888';
    footer.style.fontSize = '11px';
    footer.style.textAlign = 'center';
    footer.textContent = tr.pdfFooter;
    container.appendChild(footer);

    document.body.appendChild(container);
    try {
      const canvas = await html2canvas(container, { scale: 2, backgroundColor: '#ffffff', useCORS: true });
      const imgData = canvas.toDataURL('image/jpeg', 0.92);
      const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4' });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;
      pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      pdf.save(`RegalBot-${new Date().toISOString().split('T')[0]}.pdf`);
    } finally {
      document.body.removeChild(container);
    }
  };

  const exportChatToPDF = async () => {
    if (!messages || messages.length === 0) {
      alert(t.noMessages);
      return;
    }

    const localeMap = { fr: 'fr-CA', zh: 'zh-CN', en: 'en-CA' };
    const dateString = new Date().toLocaleString(localeMap[language] || 'en-CA');

    if (language === 'zh') {
      await renderChatPDFFromHTML(messages, t, dateString);
      return;
    }

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    const maxWidth = pageWidth - margin * 2;
    let cursorY = margin;

    doc.setFillColor(74, 111, 220);
    doc.rect(0, 0, pageWidth, 25, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text(t.pdfTitle, margin, 16);
    cursorY = 35;

    doc.setTextColor(80, 80, 80);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`${t.pdfDate}: ${dateString}`, margin, cursorY);
    cursorY += 10;

    doc.setDrawColor(220, 220, 220);
    doc.line(margin, cursorY, pageWidth - margin, cursorY);
    cursorY += 8;

    messages.forEach((msg) => {
      const isUser = msg.sender === 'user';
      const label = isUser ? t.pdfUser : t.pdfBot;

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(isUser ? 30 : 74, isUser ? 100 : 111, isUser ? 200 : 220);
      
      if (cursorY > pageHeight - 30) {
        doc.addPage();
        cursorY = margin;
      }
      doc.text(label, margin, cursorY);
      cursorY += 6;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(40, 40, 40);

      const cleanText = (msg.text || '').replace(/\*\*/g, '').replace(/\*/g, '').replace(/---/g, '');
      const lines = doc.splitTextToSize(cleanText, maxWidth);

      lines.forEach((line) => {
        if (cursorY > pageHeight - 20) {
          doc.addPage();
          cursorY = margin;
        }
        doc.text(line, margin, cursorY);
        cursorY += 5;
      });

      cursorY += 6;
    });

    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(t.pdfFooter, margin, pageHeight - 8);
      doc.text(`${i} / ${totalPages}`, pageWidth - margin - 15, pageHeight - 8);
    }

    doc.save(`RegalBot-${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const openTemplatesModal = async () => {
    setShowTemplatesModal(true);
    setSelectedTemplate(null);
    setGeneratedDocument(null);
    setTemplateFieldValues({});
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/templates`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("jwt")}`,
          },
        }
      );
      setAvailableTemplates(response.data.templates || []);
    } catch (error) {
      console.error("Error fetching templates:", error);
    }
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    const initialValues = {};
    (template?.fields || []).forEach((field, idx) => {
      const k = typeof field === 'string'
        ? field
        : (field && typeof field.key === 'string' ? field.key : `field_${idx}`);
      initialValues[k] = '';
    });
    setTemplateFieldValues(initialValues);
    setGeneratedDocument(null);
  };

  const handleTemplateFieldChange = (field, value) => {
    setTemplateFieldValues(prev => ({ ...prev, [field]: value }));
  };

  const handleGenerateTemplate = async () => {
    if (!selectedTemplate) return;
    if (tokens < 15) {
      alert(t.insufficientTokens);
      return;
    }
    try {
      setGeneratingTemplate(true);
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/generate-template`,
        { templateId: selectedTemplate.id, fieldValues: templateFieldValues },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("jwt")}`,
          },
        }
      );
      setGeneratedDocument(response.data);
      setTokens(response.data.tokens);
    } catch (error) {
      console.error("Error generating template:", error);
      alert(error.response?.data?.error || t.failedGenerate);
    } finally {
      setGeneratingTemplate(false);
    }
  };

  const renderTemplatePDFFromHTML = async (genDoc) => {
    const container = document.createElement('div');
    container.style.position = 'fixed';
    container.style.left = '-10000px';
    container.style.top = '0';
    container.style.width = '780px';
    container.style.padding = '32px';
    container.style.background = '#ffffff';
    container.style.fontFamily = "'PingFang SC','Microsoft YaHei','Hiragino Sans GB','Source Han Sans SC','Noto Sans CJK SC','WenQuanYi Micro Hei',Arial,sans-serif";
    container.style.color = '#222';
    container.style.fontSize = '14px';
    container.style.lineHeight = '1.6';

    const title = document.createElement('h1');
    title.style.fontSize = '20px';
    title.style.marginBottom = '12px';
    title.style.borderBottom = '2px solid #4a6fdc';
    title.style.paddingBottom = '8px';
    title.textContent = genDoc.templateName;
    container.appendChild(title);

    const body = document.createElement('pre');
    body.style.whiteSpace = 'pre-wrap';
    body.style.wordBreak = 'break-word';
    body.style.fontFamily = "'PingFang SC','Microsoft YaHei','Hiragino Sans GB','Source Han Sans SC','Noto Sans CJK SC','WenQuanYi Micro Hei',Arial,sans-serif";
    body.style.fontSize = '13px';
    body.style.margin = '0';
    body.textContent = (genDoc.document || '').replace(/```/g, '').trim();
    container.appendChild(body);

    document.body.appendChild(container);
    try {
      const canvas = await html2canvas(container, { scale: 2, backgroundColor: '#ffffff', useCORS: true });
      const imgData = canvas.toDataURL('image/jpeg', 0.92);
      const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4' });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;
      pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      pdf.save(`${(genDoc.templateName || 'document').replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.pdf`);
    } finally {
      document.body.removeChild(container);
    }
  };

  const downloadGeneratedTemplateAsPDF = async () => {
    if (!generatedDocument) return;
    if (language === 'zh') {
      await renderTemplatePDFFromHTML(generatedDocument);
      return;
    }
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    const maxWidth = pageWidth - margin * 2;
    let cursorY = margin;

    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(generatedDocument.templateName, margin, cursorY);
    cursorY += 10;

    doc.setDrawColor(220, 220, 220);
    doc.line(margin, cursorY, pageWidth - margin, cursorY);
    cursorY += 8;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const cleanText = (generatedDocument.document || '').replace(/\*\*/g, '').replace(/\*/g, '');
    const lines = doc.splitTextToSize(cleanText, maxWidth);
    lines.forEach((line) => {
      if (cursorY > pageHeight - 20) {
        doc.addPage();
        cursorY = margin;
      }
      doc.text(line, margin, cursorY);
      cursorY += 5;
    });

    doc.save(`${generatedDocument.templateName.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const handleSend = async () => {
    if (isListening) {
      try { recognitionRef.current && recognitionRef.current.stop(); } catch (e) {}
    }
    if (tokens !== 0) {
      if (input.trim()) {
        const newMessage = { text: input, sender: "user" };
        setMessages([...messages, newMessage]);

        const payload = { uuid_var: chatUuid, prompt: input, useDocument: !!activeDocument };

        try {
          setLoading(true);
          setInput("");
          setFollowUpQuestions([]);
          const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/query`,
            {
              method: "POST",
              headers: {
                Authorization: `Bearer ${localStorage.getItem("jwt")}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify(payload),
            }
          );

          if (!response.ok) throw new Error("Failed to fetch response");

          const data = await response.json();
          const botResponse = { 
            text: data.response, 
            sender: "bot",
            classification: data.classification,
            verifiedSources: data.verifiedSources || [],
            documentReferences: data.documentReferences || []
          };
          setSavedChats([
            ...savedChats,
            {
              id: savedChats.length + 1,
              name: `Chat ${savedChats.length + 1}`,
              input: newMessage,
              response: botResponse,
            },
          ]);
          setMessages((prevMessages) => [...prevMessages, botResponse]);
          setTokens(data.tokens);
          setLastClassification(data.classification);
          if (data.followUpQuestions) {
            setFollowUpQuestions(data.followUpQuestions);
          }
        } catch (error) {
          console.error("Error fetching response:", error);
        } finally {
          setLoading(false);
        }
      }
    } else {
      alert("Become a Premium member");
      navigate("/pricing");
    }
  };

  const handleChatSelect = (chatData) => {
    setMessages([]);
    setFollowUpQuestions([]);
    setLastClassification(null);
    if (chatData && Array.isArray(chatData)) {
      const msgs = chatData
        .map((chat) => [
          {
            text: chat.prompt,
            sender: "user",
          },
          {
            text: chat.generated_text,
            sender: "bot",
          },
        ])
        .flat();
      setMessages(msgs);
    } else {
      console.error("chatData is not in the expected format:", chatData);
    }
  };

  const analyzeDocumentFile = async (file) => {
    const formData = new FormData();
    formData.append('document', file);

    try {
      setAnalyzingDocument(true);
      setDocumentAnalysis(null);

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/analyze-document`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${localStorage.getItem("jwt")}`,
          },
        }
      );

      setDocumentAnalysis(response.data);
      setTokens(response.data.tokens);
      setDocModalTab('analysis');
      setSelectedClauseIndex(null);
      setCopiedClauseIndex(null);
      setTimelineFilter('all');
      setShowDocumentModal(true);
      return response.data;
    } catch (error) {
      console.error("Error analyzing document:", error);
      alert(error.response?.data?.error || "Failed to analyze document. Please try again.");
      return null;
    } finally {
      setAnalyzingDocument(false);
    }
  };

  const handleDocumentUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (tokens < 20) {
      alert("Document analysis requires 20 tokens. Please upgrade your plan.");
      return;
    }

    await analyzeDocumentFile(file);
    event.target.value = '';
  };

  // Load the bundled demo contract through the free, cached demo endpoint so
  // users can try the Risk Heatmap instantly without spending tokens.
  const runDemoContract = async () => {
    if (analyzingDocument) return null;
    try {
      setAnalyzingDocument(true);
      setDocumentAnalysis(null);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/demo-document`,
        { headers: { Authorization: `Bearer ${localStorage.getItem("jwt")}` } }
      );
      setDocumentAnalysis(response.data);
      setTokens(response.data.tokens);
      setDocModalTab('analysis');
      setSelectedClauseIndex(null);
      setCopiedClauseIndex(null);
      setTimelineFilter('all');
      setShowDocumentModal(true);
      return response.data;
    } catch (e) {
      console.error('Failed to load demo contract:', e);
      alert(t.heatmap.demoFailed);
      return null;
    } finally {
      setAnalyzingDocument(false);
    }
  };

  // Demo deep-link: ?demo=heatmap (or ?demo=timeline) auto-runs the bundled
  // contract and opens the requested view. Heatmap links may optionally include
  // a clause index for documentation captures.
  const demoRanRef = useRef(false);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const demoTarget = params.get('demo');
    if (!['heatmap', 'timeline'].includes(demoTarget) || demoRanRef.current) return;
    demoRanRef.current = true;
    (async () => {
      const data = await runDemoContract();
      if (!data) return;

      const requestedTab = params.get('tab');
      if ((demoTarget === 'timeline' || requestedTab === 'timeline') && data.timeline) {
        setDocModalTab('timeline');
      } else if (data.heatmap) {
        if (requestedTab !== 'analysis') setDocModalTab('heatmap');
        const clause = parseInt(params.get('clause'), 10);
        if (Number.isInteger(clause)) {
          setSelectedClauseIndex(clause);
          // Bring the expanded clause detail into view as soon as it renders.
          const tryScroll = (attempts) => {
            const el = document.querySelector('.clause-detail-panel');
            if (el) {
              el.scrollIntoView({ block: 'center' });
            } else if (attempts > 0) {
              setTimeout(() => tryScroll(attempts - 1), 100);
            }
          };
          tryScroll(50);
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startDocumentChat = () => {
    const name = documentAnalysis?.documentName || documentAnalysis?.filename;
    if (!name) return;
    setActiveDocument({ name });
    setShowDocumentModal(false);
  };

  const clearActiveDocument = async () => {
    setActiveDocument(null);
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/clear-document`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("jwt")}`,
          },
        }
      );
    } catch (error) {
      console.error("Error clearing document:", error);
    }
  };

  const copyClauseSuggestion = async (clause) => {
    if (!clause?.suggestion) return;
    try {
      await navigator.clipboard.writeText(clause.suggestion);
    } catch (e) {
      // Fallback for browsers without clipboard API permissions.
      const ta = document.createElement('textarea');
      ta.value = clause.suggestion;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch (e2) {}
      document.body.removeChild(ta);
    }
    setCopiedClauseIndex(clause.index);
    setTimeout(() => setCopiedClauseIndex((cur) => (cur === clause.index ? null : cur)), 2000);
  };

  const getRiskColor = (risk) => {
    switch (risk?.toLowerCase()) {
      case 'high': return '#e74c3c';
      case 'medium': return '#f39c12';
      case 'low': return '#27ae60';
      default: return '#3498db';
    }
  };

  const getTimelineDateStatus = (item) => {
    if (item?.timingType !== 'fixed' || !/^\d{4}-\d{2}-\d{2}$/.test(item?.date || '')) {
      return 'undated';
    }
    const today = new Date();
    const localToday = [
      today.getFullYear(),
      String(today.getMonth() + 1).padStart(2, '0'),
      String(today.getDate()).padStart(2, '0')
    ].join('-');
    if (item.date < localToday) return 'past';
    if (item.date === localToday) return 'today';
    return 'future';
  };

  const formatTimelineDate = (item) => {
    if (item?.timingType !== 'fixed' || !item?.date) {
      return item?.displayDate || t.timeline.noDate;
    }
    const date = new Date(`${item.date}T00:00:00`);
    if (Number.isNaN(date.getTime())) return item.displayDate || t.timeline.noDate;
    const locale = language === 'fr' ? 'fr-CA' : language === 'zh' ? 'zh-CN' : 'en-CA';
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(date);
  };

  const getTimelineIcon = (category) => {
    switch (category) {
      case 'payment': return '💳';
      case 'renewal': return '🔄';
      case 'termination': return '🛑';
      case 'notice': return '✉️';
      case 'deadline': return '⏰';
      default: return '✅';
    }
  };

  const getVisibleTimelineItems = () => {
    const items = Array.isArray(documentAnalysis?.timeline?.items)
      ? [...documentAnalysis.timeline.items]
      : [];
    const filtered = items.filter((item) => {
      if (timelineFilter === 'payments') return item.category === 'payment';
      if (timelineFilter === 'renewals') return item.category === 'renewal';
      if (timelineFilter === 'upcoming') {
        const status = getTimelineDateStatus(item);
        return status === 'future' || status === 'today'
          || item.timingType === 'relative'
          || item.timingType === 'recurring';
      }
      return true;
    });

    const timingRank = { fixed: 0, relative: 1, recurring: 2, none: 3 };
    return filtered.sort((a, b) => {
      const aStatus = getTimelineDateStatus(a);
      const bStatus = getTimelineDateStatus(b);
      if (aStatus === 'past' && bStatus !== 'past') return 1;
      if (bStatus === 'past' && aStatus !== 'past') return -1;
      if (a.timingType === 'fixed' && b.timingType === 'fixed') {
        return String(a.date).localeCompare(String(b.date));
      }
      const rankDiff = (timingRank[a.timingType] ?? 4) - (timingRank[b.timingType] ?? 4);
      return rankDiff || (a.clauseIndex - b.clauseIndex);
    });
  };

  const downloadTimelineCalendar = () => {
    const items = getFixedTimelineItems(documentAnalysis?.timeline?.items);
    if (items.length === 0) return;

    const documentName = documentAnalysis.documentName || documentAnalysis.filename || 'contract';
    const calendar = buildTimelineCalendar({
      items,
      documentName,
      responsibleLabel: t.timeline.responsible,
      sourceClauseLabel: t.timeline.sourceClause,
      categoryLabels: t.timeline.categories,
    });

    const blob = new Blob([calendar], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const safeName = documentName.replace(/\.[^.]+$/, '').replace(/[^a-z0-9_-]+/gi, '-');
    link.href = url;
    link.download = `${safeName || 'contract'}-key-dates.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const openCompareModal = () => {
    setCompareFileA(null);
    setCompareFileB(null);
    setComparisonResult(null);
    setShowCompareModal(true);
  };

  const resetCompare = () => {
    setCompareFileA(null);
    setCompareFileB(null);
    setComparisonResult(null);
  };

  const handleCompareDocuments = async () => {
    if (!compareFileA || !compareFileB) {
      alert(t.compareModal.needTwo);
      return;
    }
    if (tokens < 25) {
      alert(t.compareModal.insufficientTokens);
      return;
    }

    const formData = new FormData();
    formData.append('documentA', compareFileA);
    formData.append('documentB', compareFileB);

    try {
      setComparingDocuments(true);
      setComparisonResult(null);
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/compare-documents`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${localStorage.getItem("jwt")}`,
          },
        }
      );
      setComparisonResult(response.data);
      setTokens(response.data.tokens);
    } catch (error) {
      console.error("Error comparing documents:", error);
      alert(error.response?.data?.error || t.compareModal.failed);
    } finally {
      setComparingDocuments(false);
    }
  };

  const exportComparisonToPDF = async () => {
    if (!comparisonResult) return;
    const cm = t.compareModal;
    const cmp = comparisonResult.comparison || {};
    const diffLabel = { added: cm.added, removed: cm.removed, changed: cm.changed };

    const container = document.createElement('div');
    container.style.position = 'fixed';
    container.style.left = '-10000px';
    container.style.top = '0';
    container.style.width = '780px';
    container.style.background = '#ffffff';
    container.style.fontFamily = "'PingFang SC','Microsoft YaHei','Hiragino Sans GB','Source Han Sans SC','Noto Sans CJK SC','WenQuanYi Micro Hei',Arial,sans-serif";
    container.style.color = '#222';
    container.style.fontSize = '14px';
    container.style.lineHeight = '1.55';

    const esc = (s) => String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const riskColor = (r) => getRiskColor(r);

    const diffs = Array.isArray(cmp.differences) ? cmp.differences : [];
    const diffsHtml = diffs.length
      ? diffs.map((d) => `
          <div style="border:1px solid #e3e3e3;border-left:4px solid ${riskColor(d.risk)};border-radius:8px;padding:10px 12px;margin-bottom:10px;">
            <div style="font-weight:bold;margin-bottom:4px;">
              <span style="font-size:11px;text-transform:uppercase;color:#555;">${esc(diffLabel[d.type] || d.type)}</span>
              &nbsp; ${esc(d.title)}
            </div>
            <div style="color:#333;">${esc(d.explanation)}</div>
            ${d.risk && d.risk !== 'none' ? `<div style="margin-top:6px;font-size:12px;color:${riskColor(d.risk)};font-weight:bold;">${esc(cm.risk)}: ${esc(d.risk)}</div>` : ''}
          </div>`).join('')
      : `<p>${esc(cm.noDifferences)}</p>`;

    const listBlock = (titleText, arr) => (Array.isArray(arr) && arr.length)
      ? `<div style="margin-top:14px;"><h3 style="font-size:15px;color:#b26a00;margin:0 0 6px;">${esc(titleText)}</h3><ul style="margin:0;padding-left:20px;">${arr.map((x) => `<li>${esc(x)}</li>`).join('')}</ul></div>`
      : '';

    container.innerHTML = `
      <div style="background:#4a6fdc;color:#fff;padding:18px 24px;font-size:20px;font-weight:bold;">${esc(cm.pdfTitle)}</div>
      <div style="padding:12px 24px 6px;color:#555;font-size:12px;border-bottom:1px solid #ddd;">
        ${esc(comparisonResult.documentA)} &nbsp;↔&nbsp; ${esc(comparisonResult.documentB)}
      </div>
      <div style="padding:16px 24px;">
        <h3 style="font-size:15px;color:#4a6fdc;margin:0 0 6px;">${esc(cm.summary)}</h3>
        <p style="margin:0 0 14px;">${esc(cmp.summary)}</p>
        <h3 style="font-size:15px;color:#4a6fdc;margin:0 0 8px;">${esc(cm.differences)}</h3>
        ${diffsHtml}
        ${listBlock(cm.missingInA, cmp.missingInA)}
        ${listBlock(cm.missingInB, cmp.missingInB)}
      </div>
      <div style="padding:10px 24px 18px;border-top:1px solid #ddd;color:#888;font-size:11px;text-align:center;">${esc(t.pdfFooter)}</div>
    `;

    document.body.appendChild(container);
    try {
      const canvas = await html2canvas(container, { scale: 2, backgroundColor: '#ffffff', useCORS: true });
      const imgData = canvas.toDataURL('image/jpeg', 0.92);
      const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4' });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;
      pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      pdf.save(`RegalBot-Comparison-${new Date().toISOString().split('T')[0]}.pdf`);
    } finally {
      document.body.removeChild(container);
    }
  };

  const SpeechRecognitionApi =
    typeof window !== 'undefined'
      ? (window.SpeechRecognition || window.webkitSpeechRecognition)
      : null;
  const sttSupported = !!SpeechRecognitionApi;
  const ttsSupported =
    typeof window !== 'undefined' && 'speechSynthesis' in window;
  const speechLang = { en: 'en-CA', fr: 'fr-CA', zh: 'zh-CN' }[language] || 'en-US';

  const toggleListening = () => {
    if (!sttSupported) return;
    if (isListening) {
      try { recognitionRef.current && recognitionRef.current.stop(); } catch (e) {}
      return;
    }
    let recognition;
    try {
      recognition = new SpeechRecognitionApi();
    } catch (e) {
      return;
    }
    recognition.lang = speechLang;
    recognition.interimResults = true;
    recognition.continuous = false;
    listeningBaseRef.current = input ? input.trim() : "";
    recognition.onresult = (event) => {
      let transcript = "";
      for (let i = 0; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript;
      }
      const base = listeningBaseRef.current;
      setInput((base ? base + " " : "") + transcript.trim());
    };
    recognition.onerror = () => setIsListening(false);
    recognition.onend = () => setIsListening(false);
    recognitionRef.current = recognition;
    try {
      recognition.start();
      setIsListening(true);
    } catch (e) {
      setIsListening(false);
    }
  };

  const stripForSpeech = (text) => {
    if (!text) return "";
    return String(text)
      .replace(/```[\s\S]*?```/g, " ")
      .replace(/!\[(.*?)\]\((.*?)\)/g, " ")
      .replace(/\[(.*?)\]\((.*?)\)/g, "$1")
      .replace(/^\s{0,3}#{1,6}\s*/gm, "")
      .replace(/^\s*[-*•]\s+/gm, "")
      .replace(/[#*_>`~|]/g, "")
      .replace(/[📚📋🔗📄✓✅⚠️⏰🔊🔈⏹🎤🔴↔]/g, "")
      .replace(/\s+/g, " ")
      .trim();
  };

  const pickVoice = (langCode) => {
    if (!ttsSupported) return null;
    const voices = window.speechSynthesis.getVoices() || [];
    const prefix = (langCode.split('-')[0] || '').toLowerCase();
    return (
      voices.find((v) => v.lang && v.lang.toLowerCase() === langCode.toLowerCase()) ||
      voices.find((v) => v.lang && v.lang.toLowerCase().startsWith(prefix)) ||
      null
    );
  };

  const stopSpeaking = () => {
    if (!ttsSupported) return;
    try { window.speechSynthesis.cancel(); } catch (e) {}
    setSpeakingIndex(null);
  };

  const speakMessage = (text, index) => {
    if (!ttsSupported) return;
    const clean = stripForSpeech(text);
    if (!clean) return;
    try { window.speechSynthesis.cancel(); } catch (e) {}
    const utterance = new window.SpeechSynthesisUtterance(clean);
    utterance.lang = speechLang;
    const voice = pickVoice(speechLang);
    if (voice) utterance.voice = voice;
    utterance.onend = () => setSpeakingIndex((cur) => (cur === index ? null : cur));
    utterance.onerror = () => setSpeakingIndex((cur) => (cur === index ? null : cur));
    setSpeakingIndex(index);
    try {
      window.speechSynthesis.speak(utterance);
    } catch (e) {
      setSpeakingIndex(null);
    }
  };

  const toggleSpeakMessage = (text, index) => {
    if (speakingIndex === index) {
      stopSpeaking();
    } else {
      speakMessage(text, index);
    }
  };

  const toggleAutoRead = () => {
    setAutoRead((prev) => {
      const next = !prev;
      try { localStorage.setItem('regalbot_autoread', next ? '1' : '0'); } catch (e) {}
      if (!next) stopSpeaking();
      return next;
    });
  };

  // Auto-read newly arrived bot messages when the toggle is on.
  useEffect(() => {
    if (!autoRead || !ttsSupported || !messages.length) return;
    const lastIdx = messages.length - 1;
    const last = messages[lastIdx];
    if (last && last.sender === 'bot' && lastSpokenRef.current !== lastIdx) {
      lastSpokenRef.current = lastIdx;
      speakMessage(last.text, lastIdx);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, autoRead, ttsSupported]);

  // Stop any speech / recognition when the component unmounts.
  useEffect(() => {
    return () => {
      try { if (ttsSupported) window.speechSynthesis.cancel(); } catch (e) {}
      try { if (recognitionRef.current) recognitionRef.current.stop(); } catch (e) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Stop active speech / recognition when the language changes so the new
  // language is used for the next interaction.
  useEffect(() => {
    stopSpeaking();
    try { if (recognitionRef.current) recognitionRef.current.stop(); } catch (e) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  // Prime the speech-synthesis voice list (some browsers populate it async).
  useEffect(() => {
    if (!ttsSupported) return;
    try {
      window.speechSynthesis.getVoices();
      window.speechSynthesis.onvoiceschanged = () => {
        window.speechSynthesis.getVoices();
      };
    } catch (e) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setChatUuid(uuidv4());
    fetchTodayHistory();
  }, []);

  // Restore the active-document chip if the session still has an uploaded
  // document (e.g. after a page reload), so chatting with it keeps working.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/active-document`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("jwt")}`,
            },
          }
        );
        if (!cancelled && data && data.activeDocument && data.activeDocument.name) {
          setActiveDocument({ name: data.activeDocument.name });
        }
      } catch (e) {}
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const fetchUserData = async () => {
      if (_auth) {
        try {
          await getCurrentUser();
          fetchTodayHistory();
        } catch (error) {
          console.error("Error fetching user data:", error);
        }
      } else {
        console.log("User is not authenticated.");
      }
    };

    fetchUserData();
  }, [_auth]);

  const fetchTodayHistory = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/responses/`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("jwt")}`,
          },
        }
      );
      const todayHistoryResponses = response.data;
      setChats((prevChats) => ({
        ...prevChats,
        "Today History": todayHistoryResponses,
      }));
    } catch (error) {
      console.error("Error fetching today history:", error);
    }
  };

  const handleSaveChat = async () => {
    setMessages([]);
    setFollowUpQuestions([]);
    setLastClassification(null);
    setChatUuid(uuidv4());
    fetchTodayHistory();
  };

  useEffect(() => {
    const token_get_func = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/test_get_token`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("jwt")}`,
            },
          }
        );
        console.log('YO RESPONES', response);
        setTokens(response.data.tokens);
        setPackageTitle(response.data.package_title);
        if (response.data.userType) {
          setUserType(response.data.userType);
        }
      } catch (error) {
        console.log('Backend API not available, using default tokens');
        setTokens(100);
      }
    };
    token_get_func();
  }, []);

  useEffect(() => {
    if (_auth) {
      axios
        .post(
          `${process.env.REACT_APP_API_URL}/api/save_subscription`,
          {
            package_title: localStorage.getItem("card_title"),
            package_price: localStorage.getItem("card_price"),
            package_interval: localStorage.getItem("card_interval"),
            paymentId: localStorage.getItem("payment_id"),
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("jwt")}`,
              "Content-Type": "application/json",
            },
          }
        )
        .then(() => {
          localStorage.removeItem("payment_id");
          fetchTodayHistory();
          setChatUuid(uuidv4());
          return axios.get(
            `${process.env.REACT_APP_API_URL}/api/test_get_token`,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${localStorage.getItem("jwt")}`,
              },
            }
          );
        })
        .then((response) => {
          setTokens(response.data.tokens);
          setPackageTitle(response.data.package_title);
        })
        .catch((error) => {
          console.error("Error saving subscription:", error);
        });
    }
  }, [_auth]);

  const currentUserType = USER_TYPES.find(t => t.id === userType);

  if (oauthProcessing) {
    return (
      <section className="dots-container">
        <div className="dot"></div>
        <div className="dot"></div>
        <div className="dot"></div>
        <div className="dot"></div>
        <div className="dot"></div>
      </section>
    );
  }

  return (
    <div className="chatBot no-scroll">
      <div style={{ marginBottom: "80px" }}>
        <Navbar />
      </div>
      <div className="row">
        <div className="col-md-3 p-0">
          <ChatSidebar 
            savedChats={savedChats}
            onNewChat={handleSaveChat}
            handleChatSelect={handleChatSelect}
            fetchTodayHistory={fetchTodayHistory}
            chats={chats}
            setChats={setChats}
            setMessages={setMessages}
            tokens={tokens}
            setTokens={setTokens}
            packageTitle={packageTitle}
          />
        </div>
        <div className="col-md-9 p-0">
          <div style={{background:'#e9eee7',padding:'9px 22px',fontSize:13,color:'#20332f',display:'flex',justifyContent:'space-between',gap:10,alignItems:'center'}}>
            <span>{language === 'fr' ? 'Regroupez versions, constats et suivis de vos contrats.' : language === 'zh' ? '集中管理合同版本、审阅发现和后续事项。' : 'Keep contract versions, findings, and follow-ups together.'}</span>
            <Link to="/workspaces" style={{color:'#315b50',fontWeight:700,whiteSpace:'nowrap'}}>{language === 'fr' ? 'Ouvrir les dossiers' : language === 'zh' ? '打开工作区' : 'Open workspaces'} →</Link>
          </div>
          <div className="consultation-mode-header">
            <div className="user-type-selector" data-tour="user-type">
              <button 
                className="user-type-button"
                onClick={() => setShowUserTypeSelector(!showUserTypeSelector)}
              >
                <span className="user-type-icon">{currentUserType?.icon}</span>
                <span className="user-type-label">{t.userTypes[userType]}</span>
                <span className="dropdown-arrow">▼</span>
              </button>
              {showUserTypeSelector && (
                <div className="user-type-dropdown">
                  {USER_TYPES.map((type) => (
                    <button
                      key={type.id}
                      className={`user-type-option ${userType === type.id ? 'active' : ''}`}
                      onClick={() => handleUserTypeChange(type.id)}
                    >
                      <span className="option-icon">{type.icon}</span>
                      <div className="option-info">
                        <span className="option-label">{t.userTypes[type.id]}</span>
                        <span className="option-desc">{t.userTypeDescriptions[type.id]}</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="header-actions">
              <div className="language-toggle" data-tour="language-toggle">
                <button
                  className={`lang-btn ${language === 'en' ? 'active' : ''}`}
                  onClick={() => handleLanguageChange('en')}
                  title="English"
                >
                  🇬🇧 EN
                </button>
                <button
                  className={`lang-btn ${language === 'fr' ? 'active' : ''}`}
                  onClick={() => handleLanguageChange('fr')}
                  title="Français"
                >
                  🇫🇷 FR
                </button>
                <button
                  className={`lang-btn ${language === 'zh' ? 'active' : ''}`}
                  onClick={() => handleLanguageChange('zh')}
                  title="中文"
                >
                  🇨🇳 中文
                </button>
              </div>

              <button
                className="header-action-btn templates-btn"
                onClick={openTemplatesModal}
                title={t.templates}
                data-tour="templates"
              >
                📑 {t.templates}
              </button>

              <button
                className="header-action-btn compare-btn"
                onClick={openCompareModal}
                title={t.compare}
                data-tour="compare-documents"
              >
                🔀 {t.compare}
              </button>

              {ttsSupported && (
                <button
                  className={`header-action-btn autoread-btn ${autoRead ? 'active' : ''}`}
                  onClick={toggleAutoRead}
                  title={autoRead ? t.voice.autoReadOn : t.voice.autoReadOff}
                  aria-pressed={autoRead}
                  data-tour="auto-read"
                >
                  {autoRead ? '🔊' : '🔈'} {t.voice.autoRead}
                </button>
              )}

              <button
                className="header-action-btn export-btn"
                onClick={exportChatToPDF}
                title={t.exportPdf}
                disabled={messages.length === 0}
                data-tour="export-pdf"
              >
                📥 PDF
              </button>

              <button
                className="tour-launch-btn"
                onClick={startTour}
                title={t.tour.launch}
                type="button"
              >
                ❓ {t.tour.launch}
              </button>
            </div>

            {lastClassification && (
              <div className="query-classification">
                <span className="category-badge">
                  {t.categories[lastClassification.category] || t.categories.general}
                </span>
                {lastClassification.urgency !== 'low' && (
                  <span className={`urgency-badge ${lastClassification.urgency}`}>
                    {lastClassification.urgency === 'high' ? t.urgent : t.timeSensitive}
                  </span>
                )}
              </div>
            )}
          </div>
          
          <div className="legal-disclaimer-banner">
            <span className="disclaimer-icon">⚖️</span>
            <span>{t.disclaimer}</span>
          </div>

          <Slider {...sliderSettings}>
            <div className="messages-container" data-tour="conversation">
              <div className="messages-list">
                {messages.length === 0 ? (
                  <div className="empty-chat-message">
                    <div style={{display:"flex", flexDirection:"column", height:"50vh", width:"100%", justifyContent:"center", alignItems:"center"}}>
                      <img style={{width:"250px"}} src="./images/message.png" alt="" />
                      <div className="welcome-text" style={{marginTop: "20px", textAlign: "center"}}>
                        <h3 style={{color: "#333", marginBottom: "10px"}}>{t.welcome}</h3>
                        <p style={{color: "#666", fontSize: "14px"}}>
                          {t.welcomeSubtitle}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  messages.map((msg, index) => (
                    <div key={index} className={`message-item ${msg.sender}`}>
                      <div className="message-content">
                        <img
                          src={
                            msg.sender === "user"
                              ? "/images/user1.png"
                              : "/images/chatgpt.svg"
                          }
                          alt="avatar"
                          className="avatar"
                        />
                        <div className="message-bubble">
                          <ReactMarkdown>{msg.text}</ReactMarkdown>
                          {msg.sender === "bot" && ttsSupported && (
                            <button
                              type="button"
                              className={`read-aloud-btn ${speakingIndex === index ? 'speaking' : ''}`}
                              onClick={() => toggleSpeakMessage(msg.text, index)}
                              title={speakingIndex === index ? t.voice.stop : t.voice.readAloud}
                              aria-label={speakingIndex === index ? t.voice.stop : t.voice.readAloud}
                            >
                              {speakingIndex === index ? `⏹ ${t.voice.stop}` : `🔊 ${t.voice.readAloud}`}
                            </button>
                          )}
                          {msg.sender === "bot" &&
                            msg.verifiedSources &&
                            msg.verifiedSources.length > 0 && (
                              <div className="verified-sources" data-tour="verified-sources">
                                <div className="verified-sources-header">
                                  <span className="verified-sources-title">
                                    🔗 {t.verifiedSourcesTitle}
                                  </span>
                                </div>
                                <ul className="verified-sources-list">
                                  {msg.verifiedSources.map((src, i) => (
                                    <li key={i} className="verified-source-item">
                                      <a
                                        href={src.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="verified-source-link"
                                      >
                                        <span className="verified-source-badge">
                                          ✓ {t.verifiedBadge}
                                        </span>
                                        <span className="verified-source-text">
                                          <span className="verified-source-name">
                                            {src.title}
                                          </span>
                                          <span className="verified-source-meta">
                                            {src.citation}
                                            {src.jurisdiction
                                              ? ` · ${src.jurisdiction}`
                                              : ""}
                                          </span>
                                        </span>
                                      </a>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          {msg.sender === "bot" &&
                            msg.documentReferences &&
                            msg.documentReferences.length > 0 && (
                              <div className="document-references">
                                <div className="document-references-header">
                                  <span className="document-references-title">
                                    📄 {t.documentRefsTitle}
                                  </span>
                                </div>
                                <ul className="document-references-list">
                                  {msg.documentReferences.map((ref, i) => (
                                    <li key={i} className="document-reference-item">
                                      <span className="document-reference-label">
                                        {ref.label}
                                      </span>
                                      <span className="document-reference-excerpt">
                                        “{ref.excerpt}”
                                      </span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {loading ? (
                <div style={{ display: "flex" }}>
                  <img
                    style={{ width: "40px", marginRight: "20px" }}
                    src="./images/chatgpt.svg"
                    alt="loading"
                  />
                  <section className="dots-container">
                    <div className="dot"></div>
                    <div className="dot"></div>
                    <div className="dot"></div>
                  </section>
                </div>
              ) : (
                <div className="loader"></div>
              )}
            </div>
          </Slider>

          {followUpQuestions.length > 0 && !loading && (
            <div className="follow-up-questions" data-tour="follow-ups">
              <span className="follow-up-label">{t.suggestedQuestions}</span>
              <div className="follow-up-buttons">
                {followUpQuestions.map((question, index) => (
                  <button
                    key={index}
                    className="follow-up-btn"
                    onClick={() => handleFollowUpClick(question)}
                  >
                    {question}
                  </button>
                ))}
              </div>
            </div>
          )}

          {activeDocument && (
            <div className="active-document-chip">
              <span className="active-document-icon">📄</span>
              <span className="active-document-text">
                {t.activeDocLabel}: <strong>{activeDocument.name}</strong>
              </span>
              <button
                type="button"
                className="active-document-clear"
                onClick={clearActiveDocument}
                title={t.clearDoc}
                aria-label={t.clearDoc}
              >
                ×
              </button>
            </div>
          )}

          <div className="input-area">
            <div className="document-upload-wrapper" data-tour="document-upload">
              <label className="document-upload-btn" title={t.analyzeDoc}>
                <input
                  type="file"
                  accept=".pdf,.txt"
                  onChange={handleDocumentUpload}
                  disabled={analyzingDocument || loading}
                  style={{ display: 'none' }}
                />
                {analyzingDocument ? '...' : '📄'}
              </label>
              <button
                type="button"
                className="demo-contract-btn"
                onClick={runDemoContract}
                disabled={analyzingDocument || loading}
                title={t.heatmap.demoButtonTitle}
              >
                🧪 {t.heatmap.demoButton}
              </button>
            </div>
            <div className="voice-input-wrapper" data-tour="voice-input">
              <button
                type="button"
                className={`mic-btn ${isListening ? 'listening' : ''}`}
                onClick={toggleListening}
                disabled={!sttSupported || loading}
                title={!sttSupported ? t.voice.unsupported : (isListening ? t.voice.listening : t.voice.startListening)}
                aria-label={!sttSupported ? t.voice.unsupported : (isListening ? t.voice.listening : t.voice.startListening)}
                aria-pressed={isListening}
              >
                {isListening ? '🔴' : '🎤'}
              </button>
            </div>
            {isListening && (
              <span className="listening-indicator" aria-live="polite">
                <span className="listening-dot"></span>
                {t.voice.listening}
              </span>
            )}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSend();
              }}
            >
              <input
                className="input-field"
                data-tour="message-input"
                type="text"
                placeholder={activeDocument ? t.docPlaceholder : t.placeholder}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                disabled={loading}
              />
              <button
                type="submit"
                className="send-button"
                disabled={loading}
              >
                <img src="/images/send.svg" alt="send" />
              </button>
            </form>
          </div>
        </div>
      </div>

      {showCompareModal && (
        <div className="document-modal-overlay" onClick={() => setShowCompareModal(false)}>
          <div className="document-modal compare-modal" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="modal-close-btn" onClick={() => setShowCompareModal(false)}>×</button>

            <div className="modal-header">
              <h2>{t.compareModal.title}</h2>
            </div>

            <div className="modal-content">
              {!comparisonResult && (
                <>
                  <p className="compare-subtitle">{t.compareModal.subtitle}</p>

                  <div className="compare-upload-grid">
                    <div className="compare-upload-slot">
                      <label className="compare-slot-label">{t.compareModal.docA}</label>
                      <label className="compare-file-btn">
                        <input
                          type="file"
                          accept=".pdf,.txt"
                          onChange={(e) => setCompareFileA(e.target.files[0] || null)}
                          style={{ display: 'none' }}
                        />
                        📄 {compareFileA ? compareFileA.name : t.compareModal.chooseFile}
                      </label>
                    </div>

                    <div className="compare-upload-slot">
                      <label className="compare-slot-label">{t.compareModal.docB}</label>
                      <label className="compare-file-btn">
                        <input
                          type="file"
                          accept=".pdf,.txt"
                          onChange={(e) => setCompareFileB(e.target.files[0] || null)}
                          style={{ display: 'none' }}
                        />
                        📄 {compareFileB ? compareFileB.name : t.compareModal.chooseFile}
                      </label>
                    </div>
                  </div>

                  <div className="modal-footer">
                    <button
                      type="button"
                      className="run-compare-btn"
                      onClick={handleCompareDocuments}
                      disabled={comparingDocuments || !compareFileA || !compareFileB}
                    >
                      {comparingDocuments ? t.compareModal.comparing : `🔀 ${t.compareModal.runCompare}`}
                    </button>
                  </div>
                </>
              )}

              {comparisonResult && comparisonResult.comparison && (
                <div className="comparison-results">
                  <div className="comparison-files-line">
                    <span className="comparison-file">{comparisonResult.documentA}</span>
                    <span className="comparison-arrow">↔</span>
                    <span className="comparison-file">{comparisonResult.documentB}</span>
                  </div>

                  <div className="analysis-section">
                    <h3>{t.compareModal.summary}</h3>
                    <p>{comparisonResult.comparison.summary}</p>
                  </div>

                  <div className="analysis-section">
                    <h3>{t.compareModal.differences}</h3>
                    {(comparisonResult.comparison.differences || []).length > 0 ? (
                      <div className="differences-list">
                        {comparisonResult.comparison.differences.map((diff, i) => (
                          <div
                            key={i}
                            className="difference-item"
                            style={{ borderLeftColor: getRiskColor(diff.risk) }}
                          >
                            <div className="difference-header">
                              <span className={`diff-type-badge ${diff.type}`}>
                                {t.compareModal[diff.type] || diff.type}
                              </span>
                              <strong>{diff.title}</strong>
                              {diff.risk && diff.risk !== 'none' && (
                                <span
                                  className="diff-risk-badge"
                                  style={{ color: getRiskColor(diff.risk), borderColor: getRiskColor(diff.risk) }}
                                >
                                  {t.compareModal.risk}: {diff.risk}
                                </span>
                              )}
                            </div>
                            <p>{diff.explanation}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="no-differences">{t.compareModal.noDifferences}</p>
                    )}
                  </div>

                  {(comparisonResult.comparison.missingInA || []).length > 0 && (
                    <div className="analysis-section warning-section">
                      <h3>{t.compareModal.missingInA}</h3>
                      <ul>
                        {comparisonResult.comparison.missingInA.map((clause, i) => (
                          <li key={i}>{clause}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {(comparisonResult.comparison.missingInB || []).length > 0 && (
                    <div className="analysis-section warning-section">
                      <h3>{t.compareModal.missingInB}</h3>
                      <ul>
                        {comparisonResult.comparison.missingInB.map((clause, i) => (
                          <li key={i}>{clause}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="modal-footer compare-results-footer">
                    <button type="button" className="run-compare-btn" onClick={exportComparisonToPDF}>
                      📥 {t.compareModal.exportPdf}
                    </button>
                    <button type="button" className="compare-secondary-btn" onClick={resetCompare}>
                      🔁 {t.compareModal.newCompare}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showDocumentModal && documentAnalysis && (
        <div className="document-modal-overlay" onClick={() => setShowDocumentModal(false)}>
          <div className="document-modal" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="modal-close-btn" onClick={() => setShowDocumentModal(false)}>×</button>
            
            <div className="modal-header">
              <h2>{t.docModal.title}</h2>
              <span className="document-type-badge">{documentAnalysis.analysis?.documentType || t.docModal.documentDefault}</span>
            </div>

            {(
              <div className="doc-modal-tabs">
                <button
                  type="button"
                  className={`doc-modal-tab ${docModalTab === 'analysis' ? 'active' : ''}`}
                  onClick={() => setDocModalTab('analysis')}
                >
                  📋 {t.heatmap.tabAnalysis}
                </button>
                <button
                  type="button"
                  className={`doc-modal-tab ${docModalTab === 'heatmap' ? 'active' : ''}`}
                  onClick={() => setDocModalTab('heatmap')}
                >
                  🌡️ {t.heatmap.tabHeatmap}
                </button>
                <button
                  type="button"
                  className={`doc-modal-tab ${docModalTab === 'timeline' ? 'active' : ''}`}
                  onClick={() => setDocModalTab('timeline')}
                >
                  🗓️ {t.timeline.tab}
                </button>
              </div>
            )}

            {docModalTab === 'heatmap' && !documentAnalysis.heatmap ? (
            <div className="modal-content heatmap-content">
              <p className="heatmap-unavailable">{t.heatmap.unavailable}</p>
            </div>
            ) : docModalTab === 'heatmap' && documentAnalysis.heatmap ? (
            <div className="modal-content heatmap-content">
              <div className="heatmap-summary-bar">
                <div
                  className="risk-score-circle"
                  style={{ borderColor: getRiskColor(documentAnalysis.heatmap.overallRisk) }}
                >
                  <span className="risk-score">{documentAnalysis.heatmap.riskScore}</span>
                  <span className="risk-label">{t.heatmap[documentAnalysis.heatmap.overallRisk] || documentAnalysis.heatmap.overallRisk}</span>
                </div>
                <div className="heatmap-counts">
                  <span className="heatmap-count high">🔴 {documentAnalysis.heatmap.counts?.high || 0} {t.heatmap.high}</span>
                  <span className="heatmap-count medium">🟠 {documentAnalysis.heatmap.counts?.medium || 0} {t.heatmap.medium}</span>
                  <span className="heatmap-count low">🟢 {documentAnalysis.heatmap.counts?.low || 0} {t.heatmap.low}</span>
                  {(documentAnalysis.heatmap.counts?.unassessed || 0) > 0 && (
                    <span className="heatmap-count unassessed">⚪ {documentAnalysis.heatmap.counts.unassessed} {t.heatmap.unassessed}</span>
                  )}
                </div>
              </div>
              {documentAnalysis.heatmap.partial && (
                <p className="heatmap-partial-notice">⚠️ {t.heatmap.partialNotice}</p>
              )}
              <p className="heatmap-hint">{t.heatmap.clickHint}</p>

              <div className="heatmap-clauses">
                {documentAnalysis.heatmap.clauses.map((clause) => (
                  <div key={clause.index} className={`heatmap-clause risk-${clause.risk} ${selectedClauseIndex === clause.index ? 'selected' : ''}`}>
                    <button
                      type="button"
                      className="heatmap-clause-text"
                      onClick={() => setSelectedClauseIndex(selectedClauseIndex === clause.index ? null : clause.index)}
                    >
                      <span className="heatmap-clause-label">
                        {t.heatmap.clause} {clause.index + 1}
                        <span className={`heatmap-risk-badge ${clause.risk}`}>{t.heatmap[clause.risk] || clause.risk}</span>
                      </span>
                      <span className="heatmap-clause-body">{clause.text}</span>
                    </button>

                    {selectedClauseIndex === clause.index && (
                      <div className="clause-detail-panel">
                        {clause.explanation && (
                          <div className="clause-detail-block">
                            <h4>💡 {t.heatmap.meaning}</h4>
                            <p>{clause.explanation}</p>
                          </div>
                        )}
                        {clause.whyRisky && (
                          <div className="clause-detail-block">
                            <h4>⚠️ {t.heatmap.whyRisky}</h4>
                            <p>{clause.whyRisky}</p>
                          </div>
                        )}
                        {clause.suggestion && (
                          <div className="clause-detail-block suggestion">
                            <h4>✏️ {t.heatmap.suggestion}</h4>
                            <p>{clause.suggestion}</p>
                            <button
                              type="button"
                              className="copy-suggestion-btn"
                              onClick={() => copyClauseSuggestion(clause)}
                            >
                              {copiedClauseIndex === clause.index ? `✓ ${t.heatmap.copied}` : `📋 ${t.heatmap.copy}`}
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
            ) : docModalTab === 'timeline' && !documentAnalysis.timeline ? (
            <div className="modal-content timeline-content">
              <p className="timeline-unavailable">{t.timeline.unavailable}</p>
            </div>
            ) : docModalTab === 'timeline' && documentAnalysis.timeline ? (
            <div className="modal-content timeline-content">
              <div className="timeline-heading">
                <div>
                  <h3>{t.timeline.title}</h3>
                  <p>{t.timeline.subtitle}</p>
                </div>
                <button
                  type="button"
                  className="calendar-export-btn"
                  onClick={downloadTimelineCalendar}
                  disabled={!documentAnalysis.timeline.items?.some((item) => item.timingType === 'fixed' && item.date)}
                  title={t.timeline.exportHint}
                >
                  📅 {t.timeline.exportCalendar}
                </button>
              </div>

              <div className="timeline-stats">
                <div className="timeline-stat total">
                  <strong>{documentAnalysis.timeline.counts?.total || 0}</strong>
                  <span>{t.timeline.all}</span>
                </div>
                <div className="timeline-stat fixed">
                  <strong>{documentAnalysis.timeline.counts?.fixed || 0}</strong>
                  <span>{t.timeline.fixed}</span>
                </div>
                <div className="timeline-stat relative">
                  <strong>{documentAnalysis.timeline.counts?.relative || 0}</strong>
                  <span>{t.timeline.relative}</span>
                </div>
                <div className="timeline-stat recurring">
                  <strong>{documentAnalysis.timeline.counts?.recurring || 0}</strong>
                  <span>{t.timeline.recurring}</span>
                </div>
              </div>

              {documentAnalysis.timeline.partial && (
                <p className="timeline-uncertain-notice">⚠️ {t.timeline.uncertainNotice}</p>
              )}

              <div className="timeline-filters" role="group" aria-label={t.timeline.title}>
                {[
                  ['all', t.timeline.all],
                  ['upcoming', t.timeline.upcoming],
                  ['payments', t.timeline.payments],
                  ['renewals', t.timeline.renewals]
                ].map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    className={timelineFilter === value ? 'active' : ''}
                    onClick={() => setTimelineFilter(value)}
                    aria-pressed={timelineFilter === value}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {getVisibleTimelineItems().length === 0 ? (
                <p className="timeline-empty">{t.timeline.empty}</p>
              ) : (
                <div className="commitment-timeline">
                  {getVisibleTimelineItems().map((item) => {
                    const dateStatus = getTimelineDateStatus(item);
                    return (
                      <article
                        key={item.index}
                        className={`timeline-item urgency-${item.urgency} ${dateStatus === 'past' ? 'is-past' : ''}`}
                      >
                        <div className="timeline-rail" aria-hidden="true">
                          <span>{getTimelineIcon(item.category)}</span>
                        </div>
                        <div className="timeline-card">
                          <div className="timeline-item-topline">
                            <span className={`timeline-date timing-${item.timingType}`}>
                              {formatTimelineDate(item)}
                            </span>
                            {dateStatus === 'past' && <span className="timeline-past-badge">{t.timeline.past}</span>}
                            {dateStatus === 'today' && <span className="timeline-today-badge">{t.timeline.today}</span>}
                            <span className={`timeline-category category-${item.category}`}>
                              {t.timeline.categories[item.category] || item.category}
                            </span>
                          </div>

                          <h4>{item.title}</h4>
                          <p className="timeline-action">{item.action}</p>

                          <div className="timeline-item-meta">
                            {item.party && (
                              <span><strong>{t.timeline.responsible}:</strong> {item.party}</span>
                            )}
                            <span className={`timeline-urgency urgency-${item.urgency}`}>
                              {t.timeline.urgency}: {t.timeline[item.urgency] || item.urgency}
                            </span>
                            {item.confidence === 'low' && (
                              <span className="timeline-confidence-low">
                                ⚠️ {t.timeline.uncertain}
                              </span>
                            )}
                          </div>

                          <details className="timeline-source">
                            <summary>{t.timeline.sourceClause} {item.clauseIndex + 1}</summary>
                            <blockquote>“{item.evidence}”</blockquote>
                            <p>{item.sourceClause}</p>
                          </details>
                        </div>
                      </article>
                    );
                  })}
                </div>
              )}

              <p className="calendar-export-hint">ℹ️ {t.timeline.exportHint}</p>
            </div>
            ) : (
            <div className="modal-content">
              <div className="analysis-section">
                <h3>{t.docModal.summary}</h3>
                <p>{documentAnalysis.analysis?.summary}</p>
              </div>

              {documentAnalysis.analysis?.keyParties?.length > 0 && (
                <div className="analysis-section">
                  <h3>{t.docModal.keyParties}</h3>
                  <div className="parties-list">
                    {documentAnalysis.analysis.keyParties.map((party, i) => (
                      <span key={i} className="party-badge">{party}</span>
                    ))}
                  </div>
                </div>
              )}

              <div className="analysis-section risk-section">
                <h3>{t.docModal.riskAssessment}</h3>
                <div className="risk-header">
                  <div 
                    className="risk-score-circle"
                    style={{ borderColor: getRiskColor(documentAnalysis.analysis?.riskAssessment?.overallRisk) }}
                  >
                    <span className="risk-score">{documentAnalysis.analysis?.riskAssessment?.riskScore || 0}</span>
                    <span className="risk-label">{documentAnalysis.analysis?.riskAssessment?.overallRisk || 'N/A'}</span>
                  </div>
                </div>
                
                {documentAnalysis.analysis?.riskAssessment?.risks?.length > 0 && (
                  <div className="risks-list">
                    {documentAnalysis.analysis.riskAssessment.risks.map((risk, i) => (
                      <div key={i} className="risk-item" style={{ borderLeftColor: getRiskColor(risk.severity) }}>
                        <strong>{risk.title}</strong>
                        <p>{risk.description}</p>
                        <div className="risk-recommendation">
                          <span>{t.docModal.recommendation}:</span> {risk.recommendation}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {documentAnalysis.analysis?.keyClauses?.length > 0 && (
                <div className="analysis-section">
                  <h3>{t.docModal.keyClauses}</h3>
                  <div className="clauses-list">
                    {documentAnalysis.analysis.keyClauses.map((clause, i) => (
                      <div key={i} className="clause-item">
                        <div className="clause-header">
                          <strong>{clause.title}</strong>
                          <span className={`importance-badge ${clause.importance}`}>{clause.importance}</span>
                        </div>
                        <p>{clause.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {documentAnalysis.analysis?.missingClauses?.length > 0 && (
                <div className="analysis-section warning-section">
                  <h3>{t.docModal.missingClauses}</h3>
                  <ul>
                    {documentAnalysis.analysis.missingClauses.map((clause, i) => (
                      <li key={i}>{clause}</li>
                    ))}
                  </ul>
                </div>
              )}

              {documentAnalysis.analysis?.recommendations?.length > 0 && (
                <div className="analysis-section">
                  <h3>{t.docModal.recommendations}</h3>
                  <ul className="recommendations-list">
                    {documentAnalysis.analysis.recommendations.map((rec, i) => (
                      <li key={i}>{rec}</li>
                    ))}
                  </ul>
                </div>
              )}

              {(documentAnalysis.documentReady || documentAnalysis.documentName || documentAnalysis.filename) && (
                <div className="modal-footer">
                  <button
                    type="button"
                    className="ask-about-doc-btn"
                    onClick={startDocumentChat}
                  >
                    💬 {t.askAboutDoc}
                  </button>
                </div>
              )}
            </div>
            )}
          </div>
        </div>
      )}

      {showTemplatesModal && (
        <div className="document-modal-overlay" onClick={() => setShowTemplatesModal(false)}>
          <div className="document-modal templates-modal" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="modal-close-btn" onClick={() => setShowTemplatesModal(false)}>×</button>

            {!selectedTemplate && !generatedDocument && (
              <>
                <div className="modal-header">
                  <h2>📑 {t.chooseTemplate}</h2>
                  <p style={{margin: 0, color: '#666', fontSize: '14px'}}>{t.chooseTemplateSubtitle}</p>
                </div>
                <div className="modal-content">
                  <div className="templates-grid">
                    {availableTemplates.map((tpl) => (
                      <button
                        key={tpl.id}
                        className="template-card"
                        onClick={() => handleTemplateSelect(tpl)}
                      >
                        <div className="template-card-icon">📄</div>
                        <div className="template-card-name">{tpl.name}</div>
                        <div className="template-card-desc">{tpl.description}</div>
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}

            {selectedTemplate && !generatedDocument && (
              <>
                <div className="modal-header">
                  <h2>{selectedTemplate.name}</h2>
                  <span className="document-type-badge">{t.fillDetails}</span>
                </div>
                <div className="modal-content">
                  <div className="template-form">
                    {selectedTemplate.fields.map((field, idx) => {
                      const key = typeof field === 'string'
                        ? field
                        : (field && typeof field.key === 'string' ? field.key : `field_${idx}`);
                      const label = typeof field === 'string'
                        ? field
                        : (field && typeof field.label === 'string' ? field.label : key);
                      return (
                        <div key={key} className="template-form-field">
                          <label htmlFor={`tpl-${key}`}>{label}</label>
                          <input
                            id={`tpl-${key}`}
                            type="text"
                            value={templateFieldValues[key] || ''}
                            onChange={(e) => handleTemplateFieldChange(key, e.target.value)}
                            placeholder={label}
                          />
                        </div>
                      );
                    })}
                  </div>
                  <div className="template-actions">
                    <button className="template-btn-secondary" onClick={() => setSelectedTemplate(null)}>
                      ← {t.back}
                    </button>
                    <button
                      className="template-btn-primary"
                      onClick={handleGenerateTemplate}
                      disabled={generatingTemplate}
                    >
                      {generatingTemplate ? t.generating : t.generate}
                    </button>
                  </div>
                </div>
              </>
            )}

            {generatedDocument && (
              <>
                <div className="modal-header">
                  <h2>{generatedDocument.templateName}</h2>
                  <span className="document-type-badge" style={{background: '#28a745'}}>✓ {t.generated}</span>
                </div>
                <div className="modal-content">
                  <div className="generated-document">
                    <pre>{generatedDocument.document}</pre>
                  </div>
                  <div className="template-actions">
                    <button className="template-btn-secondary" onClick={() => { setGeneratedDocument(null); setSelectedTemplate(null); }}>
                      ← {t.back}
                    </button>
                    <button className="template-btn-primary" onClick={downloadGeneratedTemplateAsPDF}>
                      📥 {t.download}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <OnboardingTour
        steps={tourSteps}
        run={runTour}
        onFinish={finishTour}
        labels={{
          next: t.tour.next,
          back: t.tour.back,
          skip: t.tour.skip,
          done: t.tour.done,
        }}
      />
    </div>
  );
};

export default Chatbot;
